// SPDX-License-Identifier: AGPL-3.0-or-later

import {FEED_VIDEO_HARD_MAX_BYTES} from '@fluxer/constants/src/LimitConstants';
import {ClientUploadedAttachmentRequest} from '@fluxer/schema/src/domains/message/AttachmentSchemas';
import {PresignedAttachmentUploadRequestItem} from '@fluxer/schema/src/domains/message/AttachmentUploadSchemas';
import {ReportMessageRequest} from '@fluxer/schema/src/domains/report/ReportSchemas';
import {FilenameType} from '@fluxer/schema/src/primitives/FileValidators';
import {createQueryIntegerType, DateTimeType} from '@fluxer/schema/src/primitives/QueryValidators';
import {
	createStringType,
	NonNegativeSafeIntegerType,
	SnowflakeType,
} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

const FEED_POST_MAX_MEDIA = 4;

export const FeedPostIdParam = z.object({
	post_id: SnowflakeType.describe('The ID of the post'),
});

export type FeedPostIdParam = z.infer<typeof FeedPostIdParam>;

export const FeedPostIdCommentIdParam = FeedPostIdParam.extend({
	comment_id: SnowflakeType.describe('The ID of the comment'),
});

export type FeedPostIdCommentIdParam = z.infer<typeof FeedPostIdCommentIdParam>;

export const FeedPostIdCommentIdReplyIdParam = FeedPostIdCommentIdParam.extend({
	reply_id: SnowflakeType.describe('The ID of the reply'),
});

export type FeedPostIdCommentIdReplyIdParam = z.infer<typeof FeedPostIdCommentIdReplyIdParam>;

export const FEED_VIDEO_CONTENT_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska'] as const;

// Bunny Stream GUIDs key the video rows and KV entries in lowercase, so an upper case GUID reads the same video.
const FeedVideoGuidType = z.uuid().toLowerCase();

export const FeedVideoIdParam = z.object({
	video_id: FeedVideoGuidType.describe('The ID (Bunny Stream GUID) of the video'),
});

export type FeedVideoIdParam = z.infer<typeof FeedVideoIdParam>;

export const FeedVideoPosterRequest = z.object({
	upload_filename: createStringType(1, 4096).describe(
		'Temporary upload key of one still image (PNG, JPEG, WebP or a still GIF) from POST /feed/attachments',
	),
	filename: FilenameType.describe(
		'Name of the image file, normalized like an attachment name; it ends the poster CDN key',
	),
});

export type FeedVideoPosterRequest = z.infer<typeof FeedVideoPosterRequest>;

export const FeedVideoCreateRequest = z.object({
	filename: createStringType(1, 255).describe('Name of the file being uploaded'),
	file_size: z
		.number()
		.int()
		.min(1)
		.max(FEED_VIDEO_HARD_MAX_BYTES)
		.describe(
			"Size of the file in bytes, at most the account's max_feed_video_size (500 MiB by default, 4 GiB at most); the stored original is checked again against the allowance of this start",
		),
	content_type: z.enum(FEED_VIDEO_CONTENT_TYPES).describe('MIME type of the file'),
	duration: z
		.number()
		.min(0)
		.max(86_400)
		.optional()
		.describe(
			"Length in seconds as the client read it; over the account's max_feed_video_duration answers 400 on duration (FEED_VIDEO_DURATION_EXCEEDS_LIMIT) before any upload. The encoded length is checked again",
		),
	content: createStringType(1, 2000)
		.nullish()
		.describe('Plain text of the post the video becomes once it passes processing (1-2000 characters)'),
	public: z.boolean().optional().describe('Ignored; the server sets the public bit from the author profile visibility'),
	category_id: SnowflakeType.nullish().describe(
		'A category from GET /feed/categories the post is filed under once it is published; ignored while feature_feed_categories is 0',
	),
	topic_id: SnowflakeType.nullish().describe(
		"A live topic from GET /feed/topics the post is filed under once it is published, with the topic's category in place of category_id; ignored while feature_feed_topics is 0",
	),
	poster: FeedVideoPosterRequest.optional().describe(
		"The post's poster image, scanned before the video is created; without it the post shows Bunny's thumbnail",
	),
});

export type FeedVideoCreateRequest = z.infer<typeof FeedVideoCreateRequest>;

export const FeedVideoWebhookBody = z.object({
	VideoLibraryId: z.number().int().describe('The Bunny Stream library the video belongs to'),
	VideoGuid: FeedVideoGuidType.describe('The GUID of the video'),
	Status: z.number().int().describe('The Bunny Stream video status; unknown values are acknowledged and ignored'),
});

export type FeedVideoWebhookBody = z.infer<typeof FeedVideoWebhookBody>;

export const FeedAttachmentUploadRequest = z.object({
	attachments: z
		.array(PresignedAttachmentUploadRequestItem)
		.min(1)
		.max(FEED_POST_MAX_MEDIA)
		.describe('Images to upload for one post (1-4); a video post starts at POST /feed/videos'),
});

export type FeedAttachmentUploadRequest = z.infer<typeof FeedAttachmentUploadRequest>;

export const FEED_POLL_MAX_OPTIONS = 4;

export const FeedPollCreateRequest = z.object({
	options: z
		.array(createStringType(1, 80))
		.min(2)
		.max(FEED_POLL_MAX_OPTIONS)
		.describe('The choices, 2-4, each 1-80 characters; one vote per account, not changeable'),
	duration_hours: z.number().int().min(1).max(168).describe('Hours the poll takes votes (1-168)'),
});

export type FeedPollCreateRequest = z.infer<typeof FeedPollCreateRequest>;

export const FeedPostCreateRequest = z.object({
	content: createStringType(1, 2000).nullish().describe('Plain text of the post (1-2000 characters)'),
	attachments: z
		.array(ClientUploadedAttachmentRequest)
		.max(FEED_POST_MAX_MEDIA)
		.optional()
		.describe('Images uploaded through POST /feed/attachments (at most 4); a post needs text or media'),
	public: z.boolean().optional().describe('Ignored; the server sets the public bit from the author profile visibility'),
	poll: FeedPollCreateRequest.optional().describe('A poll; needs text as the question, and can come with images'),
	category_id: SnowflakeType.nullish().describe(
		'A category from GET /feed/categories; the post is listed there while it is public. Ignored while feature_feed_categories is 0',
	),
	topic_id: SnowflakeType.nullish().describe(
		"A live topic from GET /feed/topics; the post is listed there while it is public and filed under the topic's category in place of category_id. Ignored while feature_feed_topics is 0",
	),
});

export type FeedPostCreateRequest = z.infer<typeof FeedPostCreateRequest>;

export const FeedScheduledPostCreateRequest = z.object({
	content: createStringType(1, 2000).nullish().describe('Plain text of the post (1-2000 characters)'),
	attachments: z
		.array(ClientUploadedAttachmentRequest)
		.max(FEED_POST_MAX_MEDIA)
		.optional()
		.describe('Images uploaded through POST /feed/attachments (at most 4); a post needs text or media'),
	poll: FeedPollCreateRequest.optional().describe(
		'A poll; needs text as the question, and can come with images. Its hours run from when the post is published',
	),
	category_id: SnowflakeType.nullish().describe(
		'A category from GET /feed/categories, as on POST /feed/posts; whether the category page lists it is decided at publish',
	),
	topic_id: SnowflakeType.nullish().describe(
		'A live topic from GET /feed/topics, as on POST /feed/posts; a topic no longer live at publish does not list it',
	),
	scheduled_at: DateTimeType.describe(
		'When to publish (ISO8601 or Unix milliseconds, UTC); rounded down to the minute, at least 5 minutes and at most 30 days ahead',
	),
});

export type FeedScheduledPostCreateRequest = z.infer<typeof FeedScheduledPostCreateRequest>;

export const FeedScheduledPostIdParam = z.object({
	scheduled_id: SnowflakeType.describe('The ID of the scheduled post'),
});

export type FeedScheduledPostIdParam = z.infer<typeof FeedScheduledPostIdParam>;

export const FeedPageQuery = z.object({
	before: SnowflakeType.optional().describe('Return posts older than this post ID'),
	limit: createQueryIntegerType({defaultValue: 25, minValue: 1, maxValue: 50}).describe(
		'Maximum number of posts to return (1-50)',
	),
});

export type FeedPageQuery = z.infer<typeof FeedPageQuery>;

export const FeedDiscoverQuery = z.object({
	cursor: z
		.string()
		.regex(/^\d{1,16}\.\d{1,5}(\.\d{1,10})?$/)
		.optional()
		.describe('The X-Next-Cursor value of the previous page'),
	media: z.enum(['video']).optional().describe('video: only posts with a video (the Reels tab)'),
	interests: z
		.string()
		.regex(/^\d{1,20}(,\d{1,20}){0,49}$/)
		.optional()
		.describe(
			"Comma-separated feed category IDs the viewer is interested in (at most 50; unknown ones are ignored). Send the first page's set on the next pages",
		),
	limit: createQueryIntegerType({defaultValue: 20, minValue: 1, maxValue: 50}).describe('Posts to return (1-50)'),
});

export type FeedDiscoverQuery = z.infer<typeof FeedDiscoverQuery>;

export const FeedPollVoteRequest = z.object({
	option: z
		.number()
		.int()
		.min(0)
		.max(FEED_POLL_MAX_OPTIONS - 1)
		.describe('Index of the chosen option'),
});

export type FeedPollVoteRequest = z.infer<typeof FeedPollVoteRequest>;

export const FeedViewsRequest = z.object({
	views: z
		.array(
			z.object({
				id: SnowflakeType.describe('The ID of a post the caller saw'),
				token: z.string().min(1).max(64).describe('The view_token that came with the post'),
				count: z
					.number()
					.int()
					.optional()
					.describe('Views of the post since the last batch, one each time it came on screen; the server takes 1'),
			}),
		)
		.min(1)
		.max(100)
		.describe('Posts seen since the last batch, at most 100'),
});

export type FeedViewsRequest = z.infer<typeof FeedViewsRequest>;

export const FeedCommentsQuery = z.object({
	before: SnowflakeType.optional().describe('Return comments older than this comment ID'),
	limit: createQueryIntegerType({defaultValue: 25, minValue: 1, maxValue: 50}).describe(
		'Maximum number of comments to return (1-50)',
	),
});

export type FeedCommentsQuery = z.infer<typeof FeedCommentsQuery>;

export const FeedLikersQuery = z.object({
	after: SnowflakeType.optional().describe('Return accounts with IDs above this user ID'),
	limit: createQueryIntegerType({defaultValue: 50, minValue: 1, maxValue: 100}).describe(
		'Maximum number of accounts to return (1-100)',
	),
});

export type FeedLikersQuery = z.infer<typeof FeedLikersQuery>;

export const FeedNotificationsQuery = z.object({
	before: SnowflakeType.optional().describe('Return notifications older than this notification ID'),
	limit: createQueryIntegerType({defaultValue: 25, minValue: 1, maxValue: 50}).describe(
		'Maximum number of notifications to return (1-50)',
	),
});

export type FeedNotificationsQuery = z.infer<typeof FeedNotificationsQuery>;

export const FeedNotificationAckRequest = z.object({
	read_at: z.iso
		.datetime()
		.describe(
			'ISO8601 timestamp up to which the user has seen notifications; the server keeps the later of this and its cursor, never a future time',
		),
	unread_seq: NonNegativeSafeIntegerType.describe(
		'The newest unread sequence this session had seen (READY, pushes, acks); the read covers no more than this',
	),
	unread_epoch: NonNegativeSafeIntegerType.describe(
		'The unread epoch of that sequence; under another epoch the session saw none of the current sequence',
	),
});

export type FeedNotificationAckRequest = z.infer<typeof FeedNotificationAckRequest>;

export const FeedCommentCreateRequest = z.object({
	content: createStringType(1, 1000).describe('Plain text of the comment (1-1000 characters)'),
});

export type FeedCommentCreateRequest = z.infer<typeof FeedCommentCreateRequest>;

export const ReportFeedPostRequest = z.object({
	post_id: SnowflakeType.describe('ID of the post being reported'),
	category: ReportMessageRequest.shape.category,
});

export type ReportFeedPostRequest = z.infer<typeof ReportFeedPostRequest>;

export const ReportFeedCommentRequest = ReportFeedPostRequest.extend({
	comment_id: SnowflakeType.describe('ID of the comment being reported'),
	parent_comment_id: SnowflakeType.optional().describe(
		'ID of the top-level comment when the reported comment is a reply to it',
	),
});

export type ReportFeedCommentRequest = z.infer<typeof ReportFeedCommentRequest>;
